import {
  Address,
  newAddress,
} from "@/services/backend/addresses/types/address";
import { Branch } from "../../company/types/branch";

/** Mirrors `document.BranchReferencesBase`, backend/core/document/domain/document/branch_references_base.go. */
export type BranchReferencesBase = {
  branches: BranchReference[];
};

/** Mirrors `document.BranchReference`, backend/core/document/domain/document/branch_references_base.go. */
export type BranchReference = {
  branchId: string;
  branchRole: BranchRole;
  branchAddress: Address;
};

/** Mirrors `document.BranchRole`, backend/core/document/domain/document/branch_references_base.go. */
export enum BranchRole {
  Primary = "primary",
  Supporting = "supporting",
}

export function newBranchReferenceFrom(
  branch: Branch,
  role?: BranchRole,
): BranchReference {
  return {
    branchId: branch.id,
    branchRole: role ?? BranchRole.Supporting,
    branchAddress: branch.address,
  };
}

export function primaryBranch(
  references: BranchReferencesBase,
): BranchReference | undefined {
  return references.branches.find((b) => b.branchRole === BranchRole.Primary);
}

export function addSupportingBranch(
  references: BranchReferencesBase,
  branchId: string,
): BranchReferencesBase {
  const { branches } = references;
  if (branches.some((b) => b.branchId === branchId)) {
    return { branches };
  }

  return {
    branches: [
      ...branches,
      {
        branchId,
        branchRole: BranchRole.Supporting,
        branchAddress: newAddress(),
      },
    ],
  };
}

export function addSupportingBranchFrom(
  references: BranchReferencesBase,
  branch: Branch,
): BranchReferencesBase {
  const { branches } = references;
  if (branches.some((b) => b.branchId === branch.id)) {
    return { branches };
  }

  return {
    branches: [
      ...branches,
      newBranchReferenceFrom(branch, BranchRole.Supporting),
    ],
  };
}

export function makePrimaryBranch(
  references: BranchReferencesBase,
  branchId: string,
): BranchReferencesBase {
  const { branches } = references;
  if (!branches.some((b) => b.branchId === branchId)) {
    return { branches };
  }

  return {
    branches: branches.map((b) => ({
      ...b,
      branchRole:
        b.branchId === branchId ? BranchRole.Primary : BranchRole.Supporting,
    })),
  };
}

export function canRemoveBranch(branch: BranchReference): boolean {
  return branch.branchRole !== BranchRole.Primary;
}

export function removeBranch(
  references: BranchReferencesBase,
  branchId: string,
): BranchReferencesBase {
  return {
    branches: references.branches.filter(
      (b) => b.branchId !== branchId || !canRemoveBranch(b),
    ),
  };
}

export function updateBranchAddress(
  references: BranchReferencesBase,
  branchId: string,
  branchAddress: Address,
): BranchReferencesBase {
  return {
    branches: references.branches.map((b) =>
      b.branchId === branchId ? { ...b, branchAddress } : b,
    ),
  };
}
